import React, { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { consultarChatbot } from '../api/Common/chatbot'
import { STORAGE_KEYS } from '../utils/constantes'

const MAX_HISTORIAL_ENVIADO = 20

export const ChatContext = createContext({
    abierto: false,
    cargando: false,
    pendiente: false,
    mensajes: [],
    toggle: () => null,
    abrir: () => null,
    cerrar: () => null,
    limpiar: () => null,
    enviar: () => null,
    reintentar: () => null
})

function leerMensajes() {
    try {
        const crudo = sessionStorage.getItem(STORAGE_KEYS.CHAT_MENSAJES)
        const datos = crudo ? JSON.parse(crudo) : []
        return Array.isArray(datos) ? datos : []
    } catch (error) {
        return []
    }
}

let contadorIds = 0
const nuevoId = () => `${Date.now()}-${contadorIds++}`

/**
 * Estado del asistente de consultas. Vive por encima del router porque AdminLayout
 * se remonta en cada cambio de ruta; el historial se persiste en sessionStorage
 * para sobrevivir a los links del menu lateral, que recargan la pagina.
 */
export function ChatProvider({ children }) {
    const [abierto, setAbierto] = useState(false)
    const [cargando, setCargando] = useState(false)
    const [pendiente, setPendiente] = useState(false)
    const [mensajes, setMensajes] = useState(leerMensajes)
    const abiertoRef = useRef(abierto)

    useEffect(() => {
        abiertoRef.current = abierto
        if (abierto) setPendiente(false)
    }, [abierto])

    useEffect(() => {
        try {
            sessionStorage.setItem(STORAGE_KEYS.CHAT_MENSAJES, JSON.stringify(mensajes))
        } catch (error) {
            // sin espacio o storage deshabilitado: el chat sigue funcionando en memoria
        }
    }, [mensajes])

    const toggle = useCallback(() => setAbierto(valor => !valor), [])
    const abrir = useCallback(() => setAbierto(true), [])
    const cerrar = useCallback(() => setAbierto(false), [])

    const limpiar = useCallback(() => {
        setMensajes([])
        setPendiente(false)
    }, [])

    const enviarInterno = useCallback(async (historialBase, contenido, ruta) => {
        const mensajeUsuario = { id: nuevoId(), rol: 'usuario', contenido }
        const historial = [...historialBase, mensajeUsuario]
        setMensajes(historial)
        setCargando(true)

        try {
            const payload = historial
                .slice(-MAX_HISTORIAL_ENVIADO)
                .map(({ rol, contenido: texto }) => ({ rol, contenido: texto }))
            const data = await consultarChatbot({ mensajes: payload, contexto: { ruta } })

            setMensajes(previos => [...previos, {
                id: nuevoId(),
                rol: 'asistente',
                contenido: data.respuesta,
                herramientas: data.herramientas_usadas || [],
                enlaces: data.enlaces || [],
                consultaId: data.consulta_id
            }])
            if (!abiertoRef.current) setPendiente(true)
        } catch (error) {
            const mensaje = error.message || 'No se pudo consultar al asistente.'
            toast.error(mensaje)
            setMensajes(previos => [...previos, {
                id: nuevoId(),
                rol: 'asistente',
                contenido: mensaje,
                error: true,
                reintentar: contenido,
                ruta
            }])
            if (error.status === 403) setAbierto(false)
        } finally {
            setCargando(false)
        }
    }, [])

    const enviar = useCallback((texto, ruta = '') => {
        const contenido = (texto || '').trim()
        if (!contenido || cargando) return
        // Los mensajes de error no forman parte de la conversacion que ve el modelo.
        const base = mensajes.filter(m => !m.error)
        return enviarInterno(base, contenido, ruta)
    }, [mensajes, cargando, enviarInterno])

    const reintentar = useCallback((idError) => {
        if (cargando) return
        const indice = mensajes.findIndex(m => m.id === idError)
        if (indice === -1) return
        const fallido = mensajes[indice]
        // Se saca el error y la pregunta que lo provoco, y se vuelve a mandar la misma pregunta.
        const base = mensajes
            .slice(0, indice)
            .filter(m => !m.error)
        const sinPregunta = base.length && base[base.length - 1].rol === 'usuario'
            ? base.slice(0, -1)
            : base
        return enviarInterno(sinPregunta, fallido.reintentar, fallido.ruta || '')
    }, [mensajes, cargando, enviarInterno])

    const valueContext = {
        abierto,
        cargando,
        pendiente,
        mensajes,
        toggle,
        abrir,
        cerrar,
        limpiar,
        enviar,
        reintentar
    }

    return (
        <ChatContext.Provider value={valueContext}>
            {children}
        </ChatContext.Provider>
    )
}
